import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { ConfirmDialog, DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";

interface EmbeddedOriginsTableProps {
  origins: string[];
  loading: boolean;
  loadError: boolean;
  /** Removes the origin; rejects to keep the confirmation open for another try. */
  onRemove: (origin: string) => Promise<void>;
}

/** The origins allowed to frame embedded components, each removable after a confirmation. */
const EmbeddedOriginsTable: React.FC<EmbeddedOriginsTableProps> = ({
  origins,
  loading,
  loadError,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (loadError) setConfirmRemove(null);
  }, [loadError]);

  const columns: DataColumn<string>[] = useMemo(
    () => [
      {
        id: "origin",
        header: t("Origin"),
        cell: (origin) => <Typography variant="label">{origin}</Typography>,
      },
    ],
    [t],
  );

  const rowActions = (origin: string): RowAction[] => [
    {
      label: t("Remove"),
      danger: true,
      onClick: () => setConfirmRemove(origin),
    },
  ];

  return (
    <>
      <DataTable<string>
        columns={columns}
        rows={origins}
        getRowKey={(origin) => origin}
        loading={loading}
        errorState={
          loadError ? (
            <Typography
              variant="body1"
              sx={{ color: "var(--color-text-danger)" }}
            >
              {t("EmbeddedOriginsLoadError")}
            </Typography>
          ) : undefined
        }
        emptyState={
          <Stack gap="var(--space-1)">
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              {t("No allowed origins yet")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("EmbeddedOriginsEmptySubtitle")}
            </Typography>
          </Stack>
        }
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
      />

      <ConfirmDialog
        open={confirmRemove !== null}
        tone="danger"
        title={t("Remove origin")}
        message={
          <Trans
            i18nKey="EmbeddedOriginRemoveConfirm"
            values={{ origin: confirmRemove }}
            components={{ strong: <strong /> }}
          />
        }
        confirmLabel={t("Remove")}
        cancelLabel={t("Cancel")}
        busy={removing}
        onConfirm={async () => {
          if (confirmRemove === null || removing) return;
          setRemoving(true);
          try {
            await onRemove(confirmRemove);
            setConfirmRemove(null);
          } catch {
            // Reported by the caller; staying open lets the removal be retried.
          } finally {
            setRemoving(false);
          }
        }}
        onCancel={() => {
          if (removing) return;
          setConfirmRemove(null);
        }}
      />
    </>
  );
};

export default EmbeddedOriginsTable;
